import React from "react";
import { TileData } from "../../types";
import { PipeSvg } from "./PipeSvg";

interface PuzzleTileProps {
  tile: TileData;
  live: boolean;
  distance: number;
  isSource: boolean;
  isEnd: boolean;
  row: number;
  col: number;
  onTileClick: (row: number, col: number) => void;
  won: boolean;
}

const WIN_PULSE_KEYFRAMES_ID = "electrician-win-pulse-keyframes";
if (typeof document !== "undefined" && !document.getElementById(WIN_PULSE_KEYFRAMES_ID)) {
  const styleEl = document.createElement("style");
  styleEl.id = WIN_PULSE_KEYFRAMES_ID;
  styleEl.textContent = `
    @keyframes electricianWinPulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.07); }
      100% { transform: scale(1); }
    }
  `;
  document.head.appendChild(styleEl);
}

export const PuzzleTile = React.memo(function PuzzleTile({
  tile,
  live,
  distance,
  isSource,
  isEnd,
  row,
  col,
  onTileClick,
  won,
}: PuzzleTileProps) {
  const isEmpty = !tile.isOnPath;
  const isLocked = isSource || isEnd;

  // ── Empty tile: plain inert dark panel ──────────────────────────────────────
  if (isEmpty) {
    return (
      <div
        className="relative flex items-center justify-center select-none"
        style={{
          aspectRatio: "1 / 1",
          background: "#0c1018",
          border: "1px solid rgba(255,255,255,0.03)",
        }}
      >
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: "rgba(75,85,99,0.25)" }}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative flex items-center justify-center select-none bg-black/30
        ${isLocked ? "cursor-default" : "cursor-pointer hover:bg-black/40 "}
      `}
      onClick={isLocked ? undefined : () => onTileClick(row, col)}
    >
      {/* Win pulse overlay */}
      <div
        className="absolute inset-0 rounded-md"
        style={{
          transformOrigin: "50% 50%",
          animation:
            won && live
              ? `electricianWinPulse 400ms cubic-bezier(0.4, 0, 0.2, 1) both`
              : undefined,
          animationDelay: won && live ? `${distance * 0.06}s` : undefined,
        }}
      />

      {/* Pipe SVG — rotates with spring, no padding so pipe reaches tile edge */}
      <div
        className="w-full h-full overflow-hidden"
        style={{
          transformOrigin: "50% 50%",
          transform: `rotate(${tile.displayRotation}deg)`,
          transition:
            "transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          willChange: "transform",
        }}
      >
        <PipeSvg
          type={tile.type}
          live={live}
          isSource={isSource}
          isEnd={isEnd}
        />
      </div>

      {/* START label — bottom of tile, hard to miss */}
      {isSource && (
        <div className="absolute bottom-1 left-0 right-0 flex justify-center z-10 pointer-events-none">
          <span
            className="text-[12px] font-black tracking-[0.18em] uppercase leading-none px-1.5 py-[2px] rounded-sm"
            style={{
              background: "rgba(10,18,35,0.88)",
              color: "#86efac",
              border: "1px solid rgba(74,222,128,0.45)",
              letterSpacing: "0.18em",
            }}
          >
            START
          </span>
        </div>
      )}

      {/* END label — bottom of tile, amber accent */}
      {isEnd && (
        <div className="absolute bottom-1 left-0 right-0 flex justify-center z-10 pointer-events-none">
          <span
            className="text-[12px] font-black tracking-[0.18em] uppercase leading-none px-1.5 py-[2px] rounded-sm"
            style={{
              background: "rgba(10,18,35,0.88)",
              color: live ? "#86efac" : "#fca5a5",
              border: `1px solid ${
                live ? "rgba(74,222,128,0.45)" : "rgba(239,68,68,0.45)"
              }`,
              letterSpacing: "0.18em",
              opacity: live ? 1 : 0.7,
              transition:
                "color 300ms ease, border-color 300ms ease, opacity 300ms ease",
            }}
          >
            END
          </span>
        </div>
      )}
    </div>
  );
});
